import { useEffect, useState } from 'react'
import { listMaintenanceEvents } from '~/api/maintenance'
import type { AdminMaintenanceEvent } from '~/api/types'
import { Button } from '~/components/ui/button'
import {
    DataListTable,
    type DataListColumn,
} from '~/components/ui/data-list-table'
import { SearchInput } from '~/components/ui/search-input'
import { formatDateTime, whoLabel } from '~/lib/utils'

// 유지하기 변경 이벤트 목록 테이블. limit/offset 페이지네이션 + 행 클릭 콜백.
const PAGE_SIZE = 20

function summarize(text: string | null): string {
    if (!text) return '-'
    const trimmed = text.trim()
    return trimmed.length > 80 ? `${trimmed.slice(0, 80)}…` : trimmed
}

const COLUMNS: DataListColumn<AdminMaintenanceEvent>[] = [
    {
        key: 'changeDescription',
        header: '변경 내용',
        className: 'text-foreground',
        render: (e) => summarize(e.changeDescription),
    },
    {
        key: 'owner',
        header: '사용자',
        className: 'max-w-[180px] truncate',
        title: (e) => e.ownerEmail ?? undefined,
        render: (e) => whoLabel(e.ownerName, e.ownerEmail),
    },
    {
        key: 'assessmentId',
        header: '평가 ID',
        className:
            'text-muted-foreground max-w-[160px] truncate font-mono text-xs',
        title: (e) => e.assessmentId ?? undefined,
        render: (e) => e.assessmentId ?? '-',
    },
    {
        key: 'createdAt',
        header: '생성일',
        render: (e) => formatDateTime(e.createdAt),
    },
]

export function MaintenanceTable({
    onSelect,
    selectedId,
}: {
    onSelect: (event: AdminMaintenanceEvent) => void
    selectedId?: string
}) {
    const [items, setItems] = useState<AdminMaintenanceEvent[]>([])
    const [error, setError] = useState<string | null>(null)
    const [loading, setLoading] = useState(true)
    const [q, setQ] = useState('')
    const [offset, setOffset] = useState(0)

    useEffect(() => {
        let alive = true
        setLoading(true)
        setError(null)
        listMaintenanceEvents({
            q: q.trim() || undefined,
            limit: PAGE_SIZE,
            offset,
        })
            .then((data) => {
                if (alive) setItems(data)
            })
            .catch((e) => {
                if (alive) setError(e.message)
            })
            .finally(() => {
                if (alive) setLoading(false)
            })
        return () => {
            alive = false
        }
    }, [q, offset])

    return (
        <div>
            <div className='mb-4'>
                <SearchInput
                    className='w-72'
                    value={q}
                    onChange={(v) => {
                        setOffset(0)
                        setQ(v)
                    }}
                    placeholder='변경 내용·사용자 검색'
                />
            </div>

            <DataListTable
                items={items}
                loading={loading}
                error={error}
                columns={COLUMNS}
                getRowId={(e) => e.id}
                onRowClick={onSelect}
                selectedId={selectedId}
                empty='이벤트가 없어요.'
            />

            <div className='mt-4 flex items-center justify-between'>
                <span className='text-muted-foreground text-xs'>
                    {offset + 1}–{offset + items.length}
                </span>
                <div className='flex gap-2'>
                    <Button
                        variant='outline'
                        size='sm'
                        disabled={offset === 0}
                        onClick={() =>
                            setOffset((o) => Math.max(0, o - PAGE_SIZE))
                        }>
                        이전
                    </Button>
                    <Button
                        variant='outline'
                        size='sm'
                        disabled={items.length < PAGE_SIZE}
                        onClick={() => setOffset((o) => o + PAGE_SIZE)}>
                        다음
                    </Button>
                </div>
            </div>
        </div>
    )
}
